/**
 * The banner leading the owner's history screen — one image, written on change.
 *
 * Transactional like everything else in this section: attaching, captioning,
 * changing the attribution and removing each write immediately. There is no
 * section Save to press, and folding this into the owner form's Save would
 * rebuild the trap that lost a curator's timeline — neither form draft carries
 * `historyHeroImage` any more than it carried `history`.
 *
 * Optimistic, with rollback: the slot shows the new state at once and reverts if
 * the write throws, so it never claims something the document does not hold.
 * Same shape as the `hideTimelineFromPrimaryBrand` checkbox.
 *
 * BYTES. The order is the section's delete order, and the reverse of the event
 * modal's: persist the document FIRST, then delete the file it no longer
 * references. Deleting first would strand the owner on a missing image if the
 * write then failed. Deletion is gated on `storagePrefix` — this hero's own
 * prefix, never the timeline's — so removing a hero can neither delete an event
 * photograph nor somebody else's pasted file.
 */

import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import type { TimelineEventImage } from '@watchkeeper/shared-types';
import { deleteCalibreImage } from '../../services/firebase';
import { TimelineImageSlot } from './TimelineImageSlot';

interface Props {
  value?: TimelineEventImage;
  /** Where THIS hero's bytes live, e.g. `brands/rolex/history-hero/`. */
  storagePrefix: string;
  uploadImage: (file: File | Blob, fileName?: string) => Promise<string>;
  /** Writes the field. `null` clears it — see the note in the wrappers. */
  persist: (image: TimelineEventImage | null) => Promise<void>;
}

export function TimelineHistoryHero({ value, storagePrefix, uploadImage, persist }: Props) {
  const [image, setImage] = useState<TimelineEventImage | undefined>(value);
  const [busy, setBusy] = useState(false);

  // Re-sync when the host hands down a different owner, or a fresh copy after
  // somebody else wrote the field.
  useEffect(() => {
    setImage(value);
  }, [value]);

  const change = async (next: TimelineEventImage | undefined) => {
    const previous = image;
    setImage(next);
    setBusy(true);
    try {
      await persist(next ?? null);

      // Only now that the document no longer references them, and only when
      // this hero owns them. A replacement drops the old file unconditionally:
      // nothing else can be pointing at a path under this prefix.
      const stalePath = previous?.storagePath;
      if (stalePath && stalePath !== next?.storagePath && stalePath.startsWith(storagePrefix)) {
        try {
          await deleteCalibreImage(stalePath);
        } catch (err) {
          console.warn('History hero image delete failed (non-fatal):', err);
        }
      }
    } catch (err) {
      console.error('Failed to save the history hero image:', err);
      setImage(previous);
      toast.error('Could not save that image.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded border border-gray-200 bg-gray-50 p-3 space-y-2">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-xs font-medium uppercase text-gray-500">History hero image</span>
        <span className="text-[10px] text-gray-400">
          Leads the history screen in the app. Falls back to the usual hero when empty — saves
          immediately.
        </span>
      </div>
      <TimelineImageSlot
        value={image}
        onChange={(next) => void change(next)}
        uploadImage={uploadImage}
        disabled={busy}
        previewShape="banner"
        label="history hero image"
      />
    </div>
  );
}
